import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, animate } from 'framer-motion';

// Shared landing motion language — premium, restrained, Stripe/Linear-caliber.
// Everything here respects prefers-reduced-motion (collapses to a quick fade or no-op).

// Primary easing: easeOutExpo-ish. Use everywhere for visual consistency.
export const EASE = [0.22, 1, 0.36, 1];
export const EASE_SOFT = [0.4, 0, 0.2, 1];

const VIEWPORT = { once: true, margin: '-80px' };

/**
 * Reveal — fade + rise (+ optional blur) a single element as it scrolls into view.
 * Drop-in around any block. `delay` staggers manually; for lists use <Stagger>.
 */
export const Reveal = ({
  children,
  className,
  delay = 0,
  y = 24,
  blur = false,
  duration = 0.7,
  once = true,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const hidden = reduce
    ? { opacity: 0 }
    : { opacity: 0, y, ...(blur ? { filter: 'blur(8px)' } : {}) };
  const visible = reduce
    ? { opacity: 1, transition: { duration: 0.2 } }
    : { opacity: 1, y: 0, ...(blur ? { filter: 'blur(0px)' } : {}), transition: { duration, ease: EASE, delay } };
  return (
    <motion.div
      className={className}
      initial={hidden}
      whileInView={visible}
      viewport={{ ...VIEWPORT, once }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/**
 * Stagger — container that reveals its <StaggerItem> children in sequence on scroll-in.
 */
export const Stagger = ({
  children,
  className,
  stagger = 0.09,
  delayChildren = 0.08,
  once = true,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduce ? 0 : stagger,
        delayChildren: reduce ? 0 : delayChildren,
      },
    },
  };
  return (
    <motion.div
      className={className}
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ ...VIEWPORT, once }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/**
 * StaggerItem — child of <Stagger>. Rises + fades on its turn.
 */
export const StaggerItem = ({ children, className, y = 22, ...rest }) => {
  const reduce = useReducedMotion();
  const item = {
    hidden: reduce ? { opacity: 0 } : { opacity: 0, y },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: reduce ? 0.2 : 0.6, ease: EASE },
    },
  };
  return (
    <motion.div className={className} variants={item} {...rest}>
      {children}
    </motion.div>
  );
};

/**
 * CountUp — animate a number from 0 → value when it scrolls into view.
 * Renders {prefix}{rounded value}{suffix}. Non-numeric values should not use this.
 */
export const CountUp = ({
  value,
  prefix = '',
  suffix = '',
  duration = 1.6,
  decimals = 0,
  className,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    if (reduce) {
      setDisplay(value);
      return undefined;
    }
    const controls = animate(0, value, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, reduce, duration]);

  const shown = decimals > 0 ? display.toFixed(decimals) : Math.round(display);
  return (
    <span ref={ref} className={className}>
      {prefix}{shown}{suffix}
    </span>
  );
};
